import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Textarea } from '../ui/textarea';
import { ArrowRightLeft, Trash2 } from 'lucide-react';
import { formatNumber } from '../../utils/constants';
import { getCategoryIcon, TRANSFER_ACCOUNTS, API_URL } from './helpers';

export const TransfersTab = ({ transfers, canDeleteTransfer, onRefresh }) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTransfer, setNewTransfer] = useState({ from_account: '', to_account: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] });

  const handleCreate = async (e) => {
    e.preventDefault();
    if (newTransfer.from_account === newTransfer.to_account) {
      toast.error('Cannot transfer to the same account');
      return;
    }
    try {
      await axios.post(`${API_URL}/transfers`, {
        ...newTransfer,
        amount: parseFloat(newTransfer.amount),
        date: new Date(newTransfer.date).toISOString()
      });
      toast.success('Transfer recorded successfully');
      setDialogOpen(false);
      setNewTransfer({ from_account: '', to_account: '', amount: '', description: '', date: new Date().toISOString().split('T')[0] });
      onRefresh();
    } catch (error) {
      toast.error('Failed to record transfer');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this transfer record?')) return;
    try {
      await axios.delete(`${API_URL}/transfers/${id}`);
      toast.success('Transfer deleted');
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete transfer');
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold">Internal Transfers</h3>
        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-transfer-btn"><ArrowRightLeft className="w-4 h-4 mr-2" />Record Transfer</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>Record Internal Transfer</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-2">From Account *</label>
                <Select value={newTransfer.from_account} onValueChange={(v) => setNewTransfer({...newTransfer, from_account: v})}>
                  <SelectTrigger><SelectValue placeholder="Select source" /></SelectTrigger>
                  <SelectContent>
                    {TRANSFER_ACCOUNTS.map(acc => <SelectItem key={acc} value={acc}>{acc}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">To Account *</label>
                <Select value={newTransfer.to_account} onValueChange={(v) => setNewTransfer({...newTransfer, to_account: v})}>
                  <SelectTrigger><SelectValue placeholder="Select destination" /></SelectTrigger>
                  <SelectContent>
                    {TRANSFER_ACCOUNTS.filter(a => a !== newTransfer.from_account).map(acc => <SelectItem key={acc} value={acc}>{acc}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Amount (RWF) *</label>
                <Input type="number" step="0.01" value={newTransfer.amount} onChange={(e) => setNewTransfer({...newTransfer, amount: e.target.value})} required />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <Textarea value={newTransfer.description} onChange={(e) => setNewTransfer({...newTransfer, description: e.target.value})} placeholder="e.g., Cash deposit to bank" />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Date *</label>
                <Input type="date" value={newTransfer.date} onChange={(e) => setNewTransfer({...newTransfer, date: e.target.value})} required />
              </div>
              <Button type="submit" className="w-full">Record Transfer</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Transfers Table */}
      <div className="overflow-x-auto">
        <table className="w-full" data-testid="transfers-table">
          <thead className="bg-muted/50 border-b border-border">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Date</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">From</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">To</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Amount</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-foreground">Description</th>
              <th className="px-4 py-3 text-right text-sm font-medium text-foreground">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {transfers.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No transfers recorded yet.</td></tr>
            )}
            {transfers.map((transfer) => (
              <tr key={transfer.id} className="hover:bg-muted/30 transition-colors">
                <td className="px-4 py-3 text-sm text-muted-foreground">
                  {transfer.date ? new Date(transfer.date).toLocaleDateString() : '-'}
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex items-center space-x-2">
                    {getCategoryIcon(transfer.from_account)}
                    <span>{transfer.from_account}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm">
                  <div className="flex items-center space-x-2">
                    {getCategoryIcon(transfer.to_account)}
                    <span>{transfer.to_account}</span>
                  </div>
                </td>
                <td className="px-4 py-3 text-sm font-semibold text-foreground">RWF {formatNumber(transfer.amount)}</td>
                <td className="px-4 py-3 text-sm text-muted-foreground">{transfer.description || '-'}</td>
                <td className="px-4 py-3 text-right">
                  {canDeleteTransfer && (
                    <Button variant="ghost" size="sm" onClick={() => handleDelete(transfer.id)}>
                      <Trash2 className="w-4 h-4 text-destructive" />
                    </Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};
